// components/WhyChooseUs.jsx
import React, { useState, useEffect, useRef } from 'react';
import './WhyChooseUs.css';

const WhyChooseUs = () => {
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionSubtitle, setSectionSubtitle] = useState("");
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState([]);
  const [animatedStats, setAnimatedStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    fetchWhyChooseUsData();
  }, []);

  useEffect(() => {
    if (!hasAnimated && stats.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !hasAnimated) {
              startCountAnimation();
              setHasAnimated(true);
              observer.disconnect();
            }
          });
        },
        { threshold: 0.5 }
      );

      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }

      return () => {
        if (sectionRef.current) {
          observer.unobserve(sectionRef.current);
        }
      };
    }
  }, [stats, hasAnimated]);

  const fetchWhyChooseUsData = async () => {
    try {
      setLoading(true);
      // const response = await fetch('http://localhost/ivtcms/api/rubriques/by-url/Pourquoi-nous-choisir');
      const response = await fetch(`${process.env.REACT_APP_HOTE}api/rubriques/by-url/Pourquoi-nous-choisir`,)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        const rubriqueData = data.data;
        
        // 1. Récupérer les informations de la rubrique
        setSectionTitle(rubriqueData.titre || "");
        setSectionSubtitle(rubriqueData.descriptif || "");
        
        // 2. Filtrer et organiser les données
        if (rubriqueData.articles && rubriqueData.articles.length > 0) {
          const allArticles = rubriqueData.articles;
          
          // Services : articles avec un champ descriptif rempli
          const servicesArticles = allArticles.filter(article => 
            article.descriptif && article.descriptif.trim() !== ''
          ).map((article, index) => ({
            id: article.id_article,
            title: article.titre || "",
            description: article.descriptif || article.descriptif_short || "",
            number: (index + 1).toString()
          }));
          
          // Statistiques : articles avec soustitre mais sans descriptif
          const statsArticles = allArticles.filter(article => 
            article.soustitre && article.soustitre.trim() !== '' && 
            (!article.descriptif || article.descriptif.trim() === '')
          ).map(article => {
            // Extraire la valeur numérique du soustitre
            const soustitre = article.soustitre || "";
            let numericValue = 0;
            let suffix = "";
            
            // Extraire le nombre et le suffixe
            const match = soustitre.match(/(\d+)(.*)/);
            if (match) {
              numericValue = parseInt(match[1], 10);
              suffix = match[2] || "";
            }
            
            return {
              id: article.id_article,
              label: article.titre || "",
              numericValue: numericValue,
              suffix: suffix,
              originalText: soustitre
            };
          });
          
          setServices(servicesArticles);
          setStats(statsArticles);
          
          // Initialiser les valeurs animées à 0
          setAnimatedStats(statsArticles.map(stat => ({
            ...stat,
            animatedValue: 0
          })));
        } else {
          setServices([]);
          setStats([]);
          setAnimatedStats([]);
        }
      } else {
        throw new Error('Données de rubrique non disponibles');
      }
      
    } catch (err) {
      console.error('Erreur lors de la récupération des données:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startCountAnimation = () => {
    const duration = 2000; // Durée totale de l'animation en ms
    const steps = 60; // Nombre d'étapes
    const stepDuration = duration / steps;

    stats.forEach((stat, index) => {
      if (stat.numericValue > 0) {
        let currentStep = 0;
        
        const animateStep = () => {
          currentStep++;
          const progress = currentStep / steps;
          
          // Easing function pour un effet plus naturel
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          
          const currentValue = Math.floor(stat.numericValue * easedProgress);
          
          setAnimatedStats(prev => prev.map((s, i) => {
            if (i === index) {
              return {
                ...s,
                animatedValue: currentValue
              };
            }
            return s;
          }));

          if (currentStep < steps) {
            setTimeout(animateStep, stepDuration);
          } else {
            // À la fin, s'assurer que c'est la valeur exacte
            setAnimatedStats(prev => prev.map((s, i) => {
              if (i === index) {
                return {
                  ...s,
                  animatedValue: stat.numericValue
                };
              }
              return s;
            }));
          }
        };

        // Démarrer l'animation avec un délai différent pour chaque statistique
        setTimeout(animateStep, index * 100);
      }
    });
  };

  if (loading) {
    return (
      <section className="why-choose-us-section" id="why-choose-us">
        <div className="contenir">
          {/* Placeholder pour l'en-tête */}
          <div className="section-header">
            <div className="loading-placeholder" style={{ 
              height: '40px', 
              width: '250px',
              marginBottom: '1rem',
              background: '#f0f0f0',
              borderRadius: '4px'
            }}></div>
            
            <div className="section-divider"></div>
            
            <div className="loading-placeholder" style={{ 
              height: '20px', 
              width: '80%',
              margin: '0 auto',
              background: '#f0f0f0',
              borderRadius: '4px'
            }}></div>
          </div>

          {/* Placeholder pour les services */}
          <div className="services-list">
            {[1, 2, 3].map(i => (
              <div key={i} className="service-item">
                <div className="service-number-left">
                  <div className="loading-placeholder" style={{ 
                    width: '40px', 
                    height: '40px',
                    borderRadius: '50%',
                    background: '#f0f0f0'
                  }}></div>
                </div>
                
                <div className="service-card-right">
                  <div className="service-content">
                    <div className="loading-placeholder" style={{ 
                      height: '30px', 
                      width: '70%',
                      marginBottom: '1rem',
                      background: '#f0f0f0',
                      borderRadius: '4px'
                    }}></div>
                    
                    <div className="loading-placeholder" style={{ 
                      height: '80px', 
                      background: '#f0f0f0',
                      borderRadius: '4px'
                    }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Placeholder pour les statistiques */}
          <div className="stats-section">
            <div className="stats-container">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="stat-item">
                  <div className="loading-placeholder" style={{ 
                    width: '60px', 
                    height: '60px',
                    margin: '0 auto 0.5rem',
                    borderRadius: '50%',
                    background: '#f0f0f0'
                  }}></div>
                  
                  <div className="loading-placeholder" style={{ 
                    height: '20px', 
                    width: '100px',
                    margin: '0 auto',
                    background: '#f0f0f0',
                    borderRadius: '4px'
                  }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="why-choose-us-section" id="why-choose-us">
        <div className="contenir">
          <div className="section-header">
            <h2 className="section-title">Pourquoi nous choisir?</h2>
            <div className="section-divider"></div>
            <p className="section-subtitle" style={{color: '#ff6b6b'}}>
              Erreur de chargement : {error}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="why-choose-us-section" id="why-choose-us" ref={sectionRef}>
      <div className="contenir">
        {/* En-tête de la section */}
        <div className="section-header">
          <h2 className="section-title">{sectionTitle}</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            {sectionSubtitle}
          </p>
        </div>

        {/* Liste des services avec numéros à gauche */}
        {services.length > 0 && (
          <div className="services-list">
            {services.map((service) => (
              <div key={service.id} className="service-item">
                {/* Numéro à gauche */}
                <div className="service-number-left">
                  <span className="number">{service.number}</span>
                </div>
                
                {/* Carte à droite */}
                <div className="service-card-right">
                  <div className="service-content">
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Section des statistiques avec animation */}
        {animatedStats.length > 0 && (
          <div className="stats-section">
            <div className="stats-container">
              {animatedStats.map((stat) => (
                <div key={stat.id} className="stat-item">
                  <div className="stat-number">
                    <span>{stat.animatedValue}{stat.suffix}</span>
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyChooseUs;